/**
 * Scherm 03 — Werkvloer: per kar eerst het materiaal dat erop ligt, daarna de stap die
 * nu gedaan moet worden.
 *
 * Dit is het scherm van de operator. Wat hij hier niet ziet, ziet hij ook niet in het
 * antwoord van de server: geen foutmeldingen, geen uren en geen normtijden. De volgorde
 * op de kaart is bewust vast — materiaalstaat links, uit te voeren stap altijd op
 * dezelfde plek rechts — zodat hij hem blind vindt.
 *
 * De volledige tomtom is over twee monitoren te openen: de knop "2 schermen" opent een
 * tweede venster met alleen de route, zodat de hele tomtom in één oogopslag zichtbaar
 * is naast dit scherm.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, type Weergavestap, type Werkvloer as WerkvloerData, type WerkvloerKar } from '../api';
import { tijd, useGegevens } from '../gegevens';
import { Maatlijn } from '../onderdelen/Maatlijn';
import { Bouwnummerfilter } from '../onderdelen/Bouwnummerfilter';
import { Dialoog } from '../onderdelen/Dialoog';
import { IconCamera, IconHerhaal, IconSchermen, IconVink } from '../onderdelen/Iconen';
import { Routestrip, tekstOp } from '../onderdelen/Routepaneel';
import { Stapmenu } from '../onderdelen/Stapmenu';
import { Keuzelijst } from '../onderdelen/Keuzelijst';

export function Werkvloer() {
  const { boot, kleuren, mag, meldFout, wijzigingsteller } = useGegevens();
  const [data, setData] = useState<WerkvloerData | null>(null);
  const [afdeling, setAfdeling] = useState<string | null>(null);
  const [alleenRework, setAlleenRework] = useState(false);
  const [laden, setLaden] = useState(false);
  const [stapMenu, setStapMenu] = useState<{ kar: WerkvloerKar; stap: Weergavestap } | null>(null);
  const [rework, setRework] = useState<WerkvloerKar | null>(null);
  const [bezig, setBezig] = useState<string | null>(null);

  const bouwnummer = boot?.bouwnummer;

  const haal = useCallback(async () => {
    if (!bouwnummer) return;
    setLaden(true);
    try {
      setData(await api.werkvloer(bouwnummer));
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De werkvloer kon niet geladen worden.');
    } finally {
      setLaden(false);
    }
  }, [bouwnummer, meldFout]);

  // Verversen zodra een collega iets afstreept of een rework meldt.
  useEffect(() => {
    void haal();
  }, [haal, wijzigingsteller]);

  const karren = useMemo(() => {
    const lijst = data?.karren ?? [];
    return lijst.filter(
      (kar) =>
        (!afdeling || kar.huidigeStap?.afdeling === afdeling) && (!alleenRework || kar.isRework),
    );
  }, [data, afdeling, alleenRework]);

  const afstrepen = async (kar: WerkvloerKar) => {
    if (!bouwnummer || !kar.huidigeStap) return;
    setBezig(kar.id);
    try {
      await api.afstrepen(bouwnummer, kar.id, kar.huidigeStap.sleutel);
      await haal();
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'Afstrepen lukte niet.');
    } finally {
      setBezig(null);
    }
  };

  if (!boot) return <div className="hs-leeg">Kies eerst een boot.</div>;

  return (
    <>
      <header className="hs-kop">
        <div className="hs-koprij">
          <div>
            <p className="hs-annotatie hs-kop__gebied">Productie</p>
            <h1 className="hs-h1">Werkvloer</h1>
            <Maatlijn />
            <p className="hs-subregel">
              {data?.filters.totaal ?? 0} karren open over {data?.filters.afdelingen.length ?? 0}{' '}
              afdelingen · elke kar staat bij één stap tegelijk
            </p>
            <Bouwnummerfilter />
          </div>
          <div className="hs-kopacties">
            <span className="hs-chip">
              <span className="hs-live" />
              Live {data ? `· ${tijd(data.ververstOp)}` : ''}
            </span>
            <button type="button" className="hs-knop" onClick={() => void haal()} disabled={laden}>
              <IconHerhaal /> Verversen
            </button>
          </div>
        </div>

        {/* Filterchips: alle afdelingen, dan per afdeling, dan rework in het geel. */}
        <div className="hs-knoprij" style={{ marginTop: 8 }}>
          <button
            type="button"
            className={`hs-chip${!afdeling && !alleenRework ? ' hs-chip--actief' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setAfdeling(null);
              setAlleenRework(false);
            }}
          >
            Alle afdelingen {data?.filters.totaal ?? 0}
          </button>
          {(data?.filters.afdelingen ?? []).map((a) => {
            const kleur = kleuren?.afdelingen.find((k) => k.afdeling === a.afdeling);
            return (
              <button
                key={a.afdeling}
                type="button"
                className={`hs-chip${afdeling === a.afdeling ? ' hs-chip--actief' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setAfdeling(afdeling === a.afdeling ? null : a.afdeling);
                  setAlleenRework(false);
                }}
              >
                <span className="hs-kleurvak" style={{ background: kleur?.kleur }} />
                {a.afdeling} {a.karren}
              </button>
            );
          })}
          {data && data.filters.rework > 0 ? (
            <>
              <span className="hs-scheiding" style={{ height: 20 }} />
              <button
                type="button"
                className="hs-chip"
                style={{
                  cursor: 'pointer',
                  background: kleuren?.rework.kleur,
                  borderColor: kleuren?.rework.rand,
                  color: kleuren?.rework.tekst,
                  outline: alleenRework ? '2px solid var(--primair)' : undefined,
                  outlineOffset: 1,
                }}
                onClick={() => {
                  setAlleenRework(!alleenRework);
                  setAfdeling(null);
                }}
              >
                Rework {data.filters.rework}
              </button>
            </>
          ) : null}
        </div>
      </header>

      <div className="hs-scherm">
        {karren.length === 0 ? (
          <div className="hs-leeg">
            {laden
              ? 'De werkvloer wordt geladen…'
              : 'Er staan geen karren open. Een bouwelement komt hier zodra het is vrijgegeven voor productie.'}
          </div>
        ) : (
          karren.map((kar) => (
            <Karkaart
              key={kar.id}
              kar={kar}
              reworkKleuren={kleuren?.rework}
              magAfstrepen={mag('takenAfstrepen')}
              magRework={mag('reworkStarten')}
              metUren={mag('urenZien')}
              bezig={bezig === kar.id}
              onAfstrepen={() => void afstrepen(kar)}
              onRework={() => setRework(kar)}
              onStap={(stap) => setStapMenu({ kar, stap })}
              onTweedeScherm={() =>
                window.open(
                  `${window.location.pathname}#/tomtom/${encodeURIComponent(kar.id)}`,
                  `tomtom-${kar.id}`,
                  'width=1400,height=420',
                )
              }
            />
          ))
        )}
      </div>

      {stapMenu ? (
        <Stapmenu
          bouwnummer={boot.bouwnummer}
          karId={stapMenu.kar.id}
          stap={stapMenu.stap}
          onSluiten={() => setStapMenu(null)}
          onGewijzigd={() => {
            void haal();
            setStapMenu(null);
          }}
        />
      ) : null}

      {rework ? (
        <Reworkdialoog
          bouwnummer={boot.bouwnummer}
          kar={rework}
          onSluiten={() => setRework(null)}
          onKlaar={async () => {
            setRework(null);
            await haal();
          }}
        />
      ) : null}
    </>
  );
}

/** Eén kar op de werkvloer. */
function Karkaart({
  kar,
  reworkKleuren,
  magAfstrepen,
  magRework,
  metUren,
  bezig,
  onAfstrepen,
  onRework,
  onStap,
  onTweedeScherm,
}: {
  kar: WerkvloerKar;
  reworkKleuren?: { kleur: string; tekst: string; rand: string };
  magAfstrepen: boolean;
  magRework: boolean;
  metUren: boolean;
  bezig: boolean;
  onAfstrepen: () => void;
  onRework: () => void;
  onStap: (stap: Weergavestap) => void;
  onTweedeScherm: () => void;
}) {
  const stap = kar.huidigeStap;
  const fotoOntbreekt = Boolean(stap?.fotoVerplicht && stap.fotos.length === 0);
  /*
   * De server weigert afstrepen zolang er rode waarden open staan. Dat hoort hier al
   * te blijken: anders klikt iemand op de knop en gebeurt er niets, omdat de melding
   * bovenaan het scherm verschijnt en de gebruiker naar de kar kijkt.
   */
  const openRood = kar.foutmeldingen?.filter((f) => !f.goedgekeurd) ?? [];
  const geblokkeerd = fotoOntbreekt || openRood.length > 0;

  return (
    <article className="hs-karkaart">
      <div className="hs-karkaart__lijf">
        <div className="hs-karkaart__kop">
          <span className="hs-karkaart__nummer">{kar.nummer}</span>
          {/* De karcode staat al achteraan het nummer; alleen de naam erbij. */}
          <span className="hs-muted" style={{ fontSize: 12 }}>
            {kar.karNaam}
          </span>
          {kar.reworkActief ? (
            <span
              className="hs-chip"
              style={{
                background: reworkKleuren?.kleur,
                borderColor: reworkKleuren?.rand,
                color: reworkKleuren?.tekst,
              }}
              title={kar.reworkActief.reden}
            >
              Rework · aangevraagd door {kar.reworkActief.aangevraagdDoor}
            </span>
          ) : null}
          {kar.reworkVanKarId ? (
            <span
              className="hs-chip"
              style={{
                background: reworkKleuren?.kleur,
                borderColor: reworkKleuren?.rand,
                color: reworkKleuren?.tekst,
              }}
              title={`Deze kar is de rework van ${kar.reworkVanKarId}`}
            >
              Reworkkar
            </span>
          ) : null}
          {kar.prioriteit <= 1 ? <span className="hs-chip hs-chip--fout">Spoed</span> : null}
          <span className="hs-muted" style={{ marginLeft: 'auto' }}>
            deze kar · {kar.onderdelen} onderdelen · {kar.stuks} stuks
            {metUren && kar.uren ? ` · ${String(kar.uren.resterend).replace('.', ',')} u resterend` : ''}
          </span>
          <button
            type="button"
            className="hs-knop"
            style={{ height: 26 }}
            onClick={onTweedeScherm}
            title="De volledige tomtom op een tweede monitor openen"
          >
            <IconSchermen maat={13} /> 2 schermen
          </button>
        </div>

        {/*
         * De route bovenaan, zoals de bewerkingskolommen in de Excel: eerst waar de kar
         * langs moet, daaronder wat erop ligt.
         */}
        <Routestrip route={kar.route} positie={kar.positie} />

        <div className="hs-karkaart__body">
          <div className="hs-karkaart__materiaal">
            <span className="hs-sectielabel">
              Materiaalstaat van deze kar
              {kar.bouwelementen.length > 1 ? (
                <span className="hs-muted-licht">
                  {' '}
                  · {kar.bouwelementen.length} bouwelementen
                </span>
              ) : null}
            </span>
            <table className="hs-tabel" style={{ marginTop: 4 }}>
              <tbody>
                {kar.bouwelementen.flatMap((element) => {
                  const eigen = kar.materiaalstaat.filter((r) => r.bouwelementId === element.id);
                  if (eigen.length === 0) return [];
                  return [
                    // Op één kar staan meerdere bouwelementen; de operator moet zien
                    // welk samenstel hij in handen heeft, niet alleen welk onderdeel.
                    kar.bouwelementen.length > 1 ? (
                      <tr key={element.id} className="hs-elementkop">
                        <td colSpan={5}>
                          <span className="hs-mono">{element.code}</span> {element.naam}
                        </td>
                      </tr>
                    ) : null,
                    ...eigen.map((regel) => {
                      const o = regel.onderdeel;
                      return (
                        <tr key={regel.id}>
                          <td className="hs-mono" style={{ width: 34, fontWeight: 600 }}>
                            {o.nummer}
                          </td>
                          <td>{o.benaming}</td>
                          <td className="hs-mono hs-rechts" style={{ whiteSpace: 'nowrap' }}>
                            {o.aantal} × {o.lengte} × {o.breedte} × {o.dikte}
                          </td>
                          <td className="hs-mono hs-rechts" style={{ width: 90 }}>
                            {o.materiaal}
                          </td>
                          <td className="hs-mono hs-rechts hs-muted" style={{ width: 110 }}>
                            {[o.zijden.zijde0, o.zijden.zijde5].filter(Boolean).join(' / ') || '—'}
                          </td>
                        </tr>
                      );
                    }),
                  ];
                })}
              </tbody>
            </table>
          </div>

          <div className="hs-karkaart__stap">
            <span className="hs-sectielabel">Uit te voeren stap</span>
            {stap ? (
              <>
                <div className="hs-uitvoeren hs-uitvoeren--nu" style={{ marginTop: 4 }}>
                  <button
                    type="button"
                    className="hs-uitvoeren__afdeling"
                    style={{
                      background: stap.kleur,
                      color: stap.tekstkleur ?? tekstOp(stap.kleur),
                      border: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onClick={() => onStap(stap)}
                    title="Opties en informatie bij deze stap"
                  >
                    <span className="hs-uitvoeren__stapvan">
                      Stap {kar.positie + 1} van {kar.route.length}
                    </span>
                    <span className="hs-uitvoeren__naam">{stap.kolomnaam}</span>
                  </button>
                  <div className="hs-uitvoeren__opdracht">
                    <div className="hs-uitvoeren__titel">
                      {stap.label ? `${stap.afdeling} — ${stap.label}` : stap.afdeling}
                      {stap.varianten > 1 ? (
                        <span
                          className="hs-muted"
                          style={{ fontWeight: 400 }}
                          title="De maten verschillen per onderdeel; de volledige lijst staat in het stapmenu."
                        >
                          {' '}
                          · {stap.varianten} maten
                        </span>
                      ) : null}
                    </div>
                    <div className="hs-secundair">{stap.toelichting ?? ''}</div>
                    {stap.opmerking ? (
                      <div className="hs-melding hs-melding--info" style={{ marginTop: 6 }}>
                        {stap.opmerking}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="hs-knoprij" style={{ marginTop: 7 }}>
                  {magAfstrepen ? (
                    <button
                      type="button"
                      className="hs-knop hs-knop--primair hs-knop--vloer"
                      disabled={bezig || geblokkeerd}
                      title={
                        fotoOntbreekt
                          ? 'Bij deze stap is een foto verplicht. Voeg die eerst toe via het stapmenu.'
                          : openRood.length > 0
                            ? 'Er staan rode waarden open; productievoorbereiding moet die eerst goedkeuren.'
                            : undefined
                      }
                      onClick={onAfstrepen}
                    >
                      <IconVink /> {stap.kolomnaam} afstrepen
                    </button>
                  ) : null}
                  {magRework ? (
                    <button type="button" className="hs-knop hs-knop--vloer" onClick={onRework}>
                      Rework melden
                    </button>
                  ) : null}
                  <button type="button" className="hs-knop hs-knop--vloer" onClick={() => onStap(stap)}>
                    {stap.fotoVerplicht ? <IconCamera /> : null} Opmerking
                  </button>
                </div>
                {fotoOntbreekt ? (
                  <p className="hs-muted" style={{ marginTop: 5, color: 'var(--fout-tekst)' }}>
                    Foto verplicht bij {stap.kolomnaam} — maak die eerst via het stapmenu.
                  </p>
                ) : null}
                {openRood.length > 0 ? (
                  <p style={{ marginTop: 5, color: 'var(--fout-tekst)', fontSize: 12 }}>
                    {openRood.length} {openRood.length === 1 ? 'rode waarde' : 'rode waarden'} open —
                    afstrepen kan pas als productievoorbereiding ze heeft goedgekeurd.{' '}
                    <span className="hs-muted">
                      {openRood
                        .slice(0, 2)
                        .map((f) => `${f.onderdeelNummer}: ${f.uitleg}`)
                        .join(' · ')}
                      {openRood.length > 2 ? ' …' : ''}
                    </span>
                  </p>
                ) : null}
              </>
            ) : (
              <div className="hs-melding hs-melding--info" style={{ marginTop: 4 }}>
                Deze kar is gereed.
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

/**
 * Rework melden.
 *
 * Twee vormen, precies zoals afgesproken: op een aparte kar (dan is een karnummer
 * nodig en blijft de oorspronkelijke kar doorrijden) of de hele kar terug naar de
 * eerste rework-stap. De melding verdwijnt zodra de kar terug is op zijn
 * oorspronkelijke plek; de registratie blijft op het dashboard staan.
 */
function Reworkdialoog({
  bouwnummer,
  kar,
  onSluiten,
  onKlaar,
}: {
  bouwnummer: string;
  kar: WerkvloerKar;
  onSluiten: () => void;
  onKlaar: () => Promise<void>;
}) {
  const { meldFout } = useGegevens();
  const [aparteKar, setAparteKar] = useState(true);
  const [reden, setReden] = useState('');
  const [vanaf, setVanaf] = useState(kar.route[0]?.sleutel ?? '');
  /*
   * Standaard nul. Een rework gaat meestal over één stuk; wie de hele kar opnieuw laat
   * maken vult dat in. Met de volle aantallen als standaard is één verkeerde klik genoeg
   * om alles opnieuw te laten maken.
   */
  const [aantallen, setAantallen] = useState<Record<string, string>>(() =>
    Object.fromEntries(kar.materiaalstaat.map((m) => [m.id, '0'])),
  );
  const [bezig, setBezig] = useState(false);
  const veld = useRef<HTMLTextAreaElement>(null);

  useEffect(() => veld.current?.focus(), []);

  // Een rework gaat terug in de route: alleen stappen tot en met de huidige.
  const mogelijk = kar.route.slice(0, kar.positie + 1);

  const stuks = kar.materiaalstaat
    .map((m) => ({ onderdeelId: m.id, aantal: Number(aantallen[m.id] ?? 0) }))
    .filter((s) => s.aantal > 0);

  return (
    <Dialoog
      titel={`Rework melden — ${kar.nummer} ${kar.karNaam}`}
      onSluiten={onSluiten}
      breedte={560}
      voet={
        <>
          <button type="button" className="hs-knop" onClick={onSluiten}>
            Annuleren
          </button>
          <button
            type="button"
            className="hs-knop hs-knop--primair"
            disabled={
              bezig ||
              reden.trim().length === 0 ||
              (aparteKar && stuks.length === 0)
            }
            onClick={async () => {
              setBezig(true);
              try {
                await api.rework(bouwnummer, kar.id, {
                  vanafSleutel: vanaf,
                  reden: reden.trim(),
                  aparteKar,
                  stuks: aparteKar ? stuks : undefined,
                });
                await onKlaar();
              } catch (e) {
                meldFout(e instanceof Error ? e.message : 'De rework kon niet gemeld worden.');
              } finally {
                setBezig(false);
              }
            }}
          >
            Rework starten
          </button>
        </>
      }
    >
      <p className="hs-muted">
        Gaat de rework op een aparte kar? Zo ja, geef het karnummer op. Zo nee, dan rijdt de hele kar
        terug naar de gekozen stap. De melding verdwijnt zodra de kar terug is op zijn oorspronkelijke
        plek; de registratie blijft op het dashboard staan.
      </p>

      <div className="hs-keuzeblok">
        <button
          type="button"
          className={aparteKar ? 'gekozen' : undefined}
          onClick={() => setAparteKar(true)}
        >
          Aparte kar
          <span className="hs-muted" style={{ display: 'block', fontWeight: 400 }}>
            alleen de afgekeurde stuks gaan terug
          </span>
        </button>
        <button
          type="button"
          className={!aparteKar ? 'gekozen' : undefined}
          onClick={() => setAparteKar(false)}
        >
          Hele kar terug
          <span className="hs-muted" style={{ display: 'block', fontWeight: 400 }}>
            de kar rijdt zelf terug
          </span>
        </button>
      </div>

      {aparteKar ? (
        <p className="hs-muted" style={{ marginTop: 2 }}>
          De reworkkar krijgt de code van deze kar met een <span className="hs-mono">R</span>{' '}
          erachter — <span className="hs-mono">{kar.kar}R</span> — en daarmee zijn eigen nummer in
          de boom. Er is niets in te vullen.
        </p>
      ) : null}

      <label className="hs-veld">
        <span>Terug naar</span>
        <Keuzelijst className="hs-keuze" value={vanaf} onChange={(e) => setVanaf(e.target.value)}>
          {mogelijk.map((stap) => (
            <option key={stap.sleutel} value={stap.sleutel}>
              {stap.kolomnaam} — {stap.afdeling}
            </option>
          ))}
        </Keuzelijst>
      </label>

      <label className="hs-veld">
        <span>Wat is er aan de hand?</span>
        <textarea
          ref={veld}
          className="hs-invoer"
          rows={2}
          value={reden}
          onChange={(e) => setReden(e.target.value)}
          placeholder="Bijvoorbeeld: fineer losgelaten bij het kalibreren."
        />
      </label>

      {aparteKar ? (
        <div>
          <span className="hs-sectielabel">Welke stuks gaan opnieuw?</span>
          <table className="hs-tabel" style={{ marginTop: 4 }}>
            <thead>
              <tr>
                <th>Nr</th>
                <th>Benaming</th>
                <th className="hs-rechts">Op de kar</th>
                <th style={{ width: 80 }}>Opnieuw</th>
              </tr>
            </thead>
            <tbody>
              {kar.materiaalstaat.map((m) => (
                <tr key={m.id}>
                  <td className="hs-mono">{m.onderdeel.nummer}</td>
                  <td>{m.onderdeel.benaming}</td>
                  <td className="hs-mono hs-rechts">{m.onderdeel.aantal}</td>
                  <td>
                    <input
                      className="hs-invoer hs-invoer--mono"
                      style={{ width: 60 }}
                      type="number"
                      min={0}
                      max={m.onderdeel.aantal}
                      value={aantallen[m.id] ?? ''}
                      onChange={(e) => setAantallen((v) => ({ ...v, [m.id]: e.target.value }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Dialoog>
  );
}
